<template>
  <v-modal
    :short="
      [
        STAGES.INITIAL,
        STAGES.PASSWORDLESS,
        STAGES.EMAIL,
        STAGES.DOWNLOAD,
        STAGES.NOT_RECOGNISED
      ].includes(stage)
    "
    class="auth-modal"
    :disable-close="shouldDisableClose"
    @open="handleOpen"
    @close="handleModalClose"
    @initialOpen="handleInitialOpen"
  >
    <template v-if="showOverlay" v-slot:overlay>
      <blocks-container v-if="overlayBlocks" :blocks="overlayBlocks" />
    </template>

    <template v-if="showAvatar" v-slot:before>
      <avatar :image="appIcon" :altText="appName" />
    </template>

    <template :slot="STAGES.INITIAL">
      <div class="auth-modal__content">
        <external-auth-buttons
          :is-loading="isLoading"
          :providers="enabledSocialProviders"
          @success="handleExternalAuth"
        />
        <v-button secondary block @click="goToEmail">
          {{ $t("components.authModal.continueWithEmail") }}
        </v-button>
      </div>
    </template>

    <template :slot="STAGES.PASSWORDLESS">
      <v-loading />
    </template>

    <template :slot="STAGES.TOKEN_SENT">
      <div class="auth-modal__content">
        <div class="my-4 mb-2 px-1">
          <v-icon icon="notifications_active" size="28px" />
          <h2 class="mb-1">{{ $t("components.auth.emailSent.heading") }}</h2>
          <p class="lead mb-4">
            {{ $t("components.auth.emailSent.description") }}
          </p>

          <v-button
            outlined
            secondary
            block
            class="mb-2"
            to="https://mail.google.com/mail/u/0/"
          >
            Open Gmail
          </v-button>
          <v-button
            outlined
            secondary
            block
            to="https://outlook.live.com/mail/0/inbox"
          >
            Open Outlook
          </v-button>

          <hr class="my-3" />

          <v-button
            :is-loading="isResendingEmail"
            secondary
            block
            :disabled="hasResentEmail"
            @click="resendEmail"
          >
            {{
              hasResentEmail
                ? $t("components.authModal.emailResendCheckInbox")
                : $t("components.authModal.emailResendPrompt")
            }}
          </v-button>
        </div>
      </div>
    </template>

    <template :slot="STAGES.DOWNLOAD">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.welcome", { appName: appName }) }}.
        </h2>
        <p class="lead mb-2 mt-0">
          {{ $t("components.authModal.installApp") }}
        </p>

        <add-to-homescreen context="auth_flow" />
        <v-button
          :is-loading="isLoading"
          block
          class="mt-2"
          ghost
          @click="closeModal"
        >
          {{ $t("components.authModal.installAppDecline") }}
        </v-button>
      </div>
    </template>

    <template :slot="STAGES.EMAIL">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-2">{{ $t("components.authModal.emailHeading") }}</h2>
        <form @submit.prevent="fetchUser">
          <div class="mb-2">
            <v-input
              data-role="login-email"
              required
              float
              :placeholder="$t('components.authModal.emailInputPlaceholder')"
              v-model="email"
              type="email"
              name="email"
              @input="debounce(saveLastEmail, 250)"
            />
          </div>

          {{ /* By having the submit button appear first we guarantee "Enter" focuses the right button */ }}
          <div class="d-flex flex-row-reverse">
            <v-button :is-loading="isLoading" block class="mb-1" type="submit">
              {{ $t("components.authModal.emailCta") }}
            </v-button>

            <v-button
              v-if="enabledSocialProviders.length"
              class="mr-1"
              round
              secondary
              @click.prevent="() => openModal(STAGES.INITIAL)"
            >
              <v-icon icon="arrow_back"></v-icon>
            </v-button>
          </div>
        </form>
      </div>
    </template>

    <template :slot="STAGES.LOGIN">
      <div class="auth-modal__content">
        <template v-if="providerToLink">
          <template v-if="currentProvider !== AUTH_PROVIDERS.default">
            <h2 class="mb-1 mt-2">
              {{
                $t("components.authModal.loginToLinkProvider", {
                  provider: formattedProviderToLink
                })
              }}
            </h2>
            <p class="lead mb-3 mt-0">
              {{
                $t("components.authModal.lastUsedProvider", {
                  providerToLink: formattedProviderToLink,
                  provider: formattedCurrentProvider
                })
              }}
            </p>

            <external-auth-buttons
              :is-loading="isLinkingProvider || isLoading"
              class="mb-2"
              :providers="[currentProvider]"
              @success="handleExternalAuth"
            />
          </template>
          <template v-else>
            <login-form
              :heading="
                $t('components.authModal.loginToLinkProvider', {
                  provider: formattedProviderToLink
                })
              "
              :email="email"
              @back="() => openModal(STAGES.INITIAL)"
              @login="handleLogin"
            />
            <v-button
              @click="() => openModal(STAGES.REQUEST_RESET_PASSWORD)"
              block
              ghost
            >
              {{ $t("components.authModal.forgotPasswordCta") }}
            </v-button>
          </template>
        </template>

        <template v-else>
          <login-form
            :email="email"
            @back="() => openModal(STAGES.INITIAL)"
            @login="handleLogin"
          />
          <v-button
            @click="() => openModal(STAGES.REQUEST_RESET_PASSWORD)"
            block
            ghost
          >
            {{ $t("components.authModal.forgotPasswordCta") }}
          </v-button>
        </template>
      </div>
    </template>

    <template :slot="STAGES.PROMPT_LINK_PROVIDER">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{
            $t("components.authModal.promptLinkProviderHeading", {
              providerToLink: formattedProviderToLink
            })
          }}
        </h2>
        <p class="lead mb-2 mt-0">
          {{
            $t("components.authModal.promptLinkProviderSubheading", {
              providerToLink: formattedProviderToLink
            })
          }}
        </p>
        <v-button block @click="() => openModal(STAGES.LOGIN)" class="mb-2">
          {{
            $t("components.authModal.promptLinkProviderConfirm", {
              providerToLink: formattedProviderToLink
            })
          }}
        </v-button>
        <v-button secondary block @click="resetLinkingProvider" class="mb-2">
          {{ $t("components.authModal.promptLinkProviderDismiss") }}
        </v-button>
      </div>
    </template>

    <template :slot="STAGES.RESET_PASSWORD">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.setPasswordHeading") }}
        </h2>
        <p class="lead mb-2 mt-0">
          {{ $t("components.authModal.resetPasswordDescription") }}.
        </p>
        <set-password-form @submit="attemptResetPassword" />
        <v-button @click="() => openModal(STAGES.LOGIN)" block ghost>
          {{ $t("components.authModal.resetPasswordBackCta") }}
        </v-button>
      </div>
    </template>

    <template :slot="STAGES.REQUEST_RESET_PASSWORD">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.requestResetPasswordHeading") }}
        </h2>
        <p class="lead mb-2 mt-0">
          {{ $t("components.authModal.requestResetPasswordSubheading") }}
        </p>
        <form @submit.prevent="attemptRequestResetPassword">
          <div class="mb-2">
            <v-input
              :placeholder="
                $t('components.authModal.requestResetPasswordEmailPlaceholder')
              "
              type="email"
              name="email"
              v-model="email"
            ></v-input>
          </div>
          <div class="d-flex align-items-center">
            <v-button
              round
              secondary
              class="mr-2"
              type="button"
              @click.prevent="() => openModal(STAGES.LOGIN)"
            >
              <v-icon icon="arrow_back"></v-icon>
            </v-button>
            <v-button :is-loading="isLoading" block type="submit">
              {{ $t("components.authModal.requestResetPasswordCta") }}
            </v-button>
          </div>
        </form>
      </div>
    </template>

    <template :slot="STAGES.SET_PASSWORD">
      <div class="auth-modal__content">
        <template v-if="isExistingAccount">
          <h2 class="mt-0 mb-1">
            {{ $t("components.authModal.setupAccountHeading") }}
          </h2>

          <p class="lead mb-3 mt-0">
            <template v-if="enabledSocialProviders.length">
              {{
                $t("components.authModal.setupAccountSubheadingProviders", {
                  providers: enabledSocialProviders
                    .map(capitalizeFirstLetter)
                    .join(" or ")
                })
              }}
            </template>
            <template v-else>
              {{ $t("components.authModal.setupAccountSubheadingPassword") }}
            </template>
          </p>

          <template v-if="enabledSocialProviders.length">
            <external-auth-buttons
              :is-loading="isLoading"
              @success="handleExternalAuth"
            />

            <hr class="--or my-3" />

            <v-button
              v-if="!showSetPasswordForm"
              secondary
              block
              @click="() => (showSetPasswordForm = true)"
            >
              {{ $t("components.authModal.setupAccountPasswordCta") }}
            </v-button>
          </template>

          <template
            v-if="showSetPasswordForm || !enabledSocialProviders.length"
          >
            <template v-if="enabledSocialProviders.length">
              <h2 class="mt-0 mb-1">
                {{ $t("components.authModal.setupWithPasswordHeading") }}
              </h2>
              <p class="lead mb-3 mt-0">
                {{ $t("components.authModal.setupWithPasswordSubheading") }}
              </p>
            </template>

            <create-account-form
              :cta-text="
                $t('components.authModal.setupAccountPasswordFinishCta')
              "
              :email="email"
              @submit="handleCreateAccount"
            />
          </template>
        </template>
        <template v-else>
          <h2 class="mt-0 mb-1">
            {{ $t("components.authModal.createAccountHeading") }}
          </h2>
          <p class="lead mb-3 mt-0">
            {{ $t("components.authModal.createAccountSubheading") }}
          </p>
          <create-account-form
            :email="email"
            @back="() => openModal(STAGES.EMAIL)"
            @submit="handleCreateAccount"
          />
        </template>
      </div>
    </template>

    <template :slot="STAGES.PASSWORDLESS_ERROR">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.passwordlessErrorHeading") }}
        </h2>
        <p class="lead mb-2">
          {{ $t("components.authModal.passwordlessErrorSubheading") }}
        </p>
        <form @submit.prevent="attemptRequestPasswordless">
          <div class="mb-2">
            <v-input
              :placeholder="
                $t('components.authModal.passwordlessErrorEmailPlaceholder')
              "
              v-model="email"
              type="email"
              name="email"
            ></v-input>
          </div>
          <v-button :is-loading="isLoading" block class="mb-1" type="submit">
            {{ $t("components.authModal.passwordlessErrorCta") }}
          </v-button>
        </form>
      </div>
    </template>

    <template :slot="STAGES.NOT_RECOGNISED">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.notRecognisedHeading") }}
        </h2>
        <p class="lead mt-0">
          {{ $t("components.authModal.notRecognizedSubheading") }}
        </p>

        <v-button block class="mt-2 mb-1" @click="openModal(STAGES.EMAIL)">
          {{ $t("components.authModal.notRecognizedBackCta") }}
        </v-button>
        <v-button block ghost :to="`mailto:${ownerEmail}`" target="_top">
          {{ $t("components.authModal.notRecognizedContactOwnerCta") }}
        </v-button>
      </div>
    </template>
  </v-modal>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"

import Avatar from "@/components/Avatar"
import vInput from "@/components/forms/vInput"

import EventBus, { EVENTS } from "@/lib/event-bus"
import { isPresent, findAsync, capitalizeFirstLetter } from "@/lib/utils"
import { triggerBlockAction } from "@/lib/stream-utils"
import * as LocalStorage from "@/lib/local-storage"
import debounce from "@/lib/debounce"

import AUTH_PROVIDERS from "@shared/auth-providers.json"

export const STAGES = {
  INITIAL: "#auth",
  PROMPT_LINK_PROVIDER: "#auth/link_provider",
  CONFIRM_LINK_PROVIDER: "#auth/link_provider/confirm",
  EMAIL: "#auth/email",
  LOGIN: "#auth/login",
  DOWNLOAD: "#auth/download",
  RESET_PASSWORD: "#auth/reset_password",
  REQUEST_RESET_PASSWORD: "#auth/reset_password/request",
  SET_PASSWORD: "#auth/set_password",
  TOKEN_SENT: "#auth/token_sent",
  PASSWORDLESS: "#auth/passwordless",
  PASSWORDLESS_ERROR: "#auth/passwordless/error",
  NOT_RECOGNISED: "#auth/not_recognised"
}

export default {
  data() {
    return {
      stage: null,
      name: "",
      email: "",
      password: "",
      passwordConfirmation: "",

      isLoading: false,
      isExistingAccount: false,
      isExpressAuth: false,
      isClosing: false,
      isLinkingProvider: false,

      isResendingEmail: false,
      hasResentEmail: false,

      providerToLink: null,
      providerIdToLink: null,
      currentProvider: null,

      showSetPasswordForm: false,

      STAGES,
      AUTH_PROVIDERS
    }
  },

  components: {
    vInput,
    Avatar,
    LoginForm: () => import("@/components/auth/LoginForm"),
    CreateAccountForm: () => import("@/components/auth/CreateAccountForm"),
    SetPasswordForm: () => import("@/components/auth/SetPasswordForm"),
    AddToHomescreen: () => import("@/components/AddToHomescreen"),
    BlocksContainer: () => import("@/components/stream/BlocksContainer"),
    ExternalAuthButtons: () => import("@/components/auth/ExternalAuthButtons")
  },

  computed: {
    ...mapState(["global"]),
    ...mapGetters("forms", ["authSignedUpForm"]),
    ...mapGetters("pwa", ["canPrompt"]),
    ...mapGetters("auth", ["isAuthenticated", "providers", "currentUser"]),
    ...mapGetters("access", [
      "requiresAuthentication",
      "appPurchaseOverlayId",
      "isSubscriberOnly",
      "isPrivateApp"
    ]),
    ...mapGetters("products", ["getAppProducts", "hasPastDueSubscription"]),
    ...mapGetters(["appName", "appIcon", "appAccess"]),

    overlayBlocks() {
      const overlay = this.global?.access.auth_overlay

      return overlay && overlay.stream ? overlay.stream.blocks : null
    },

    showAvatar() {
      return !this.showOverlay || this.isExpressAuth
    },

    showOverlay() {
      return (
        isPresent(this.overlayBlocks) &&
        !this.isExpressAuth &&
        [STAGES.INITIAL, STAGES.EMAIL, STAGES.SET_PASSWORD].includes(this.stage)
      )
    },

    shouldPromptPurchasePostSignup() {
      return (
        !!this.appAccess.prompt_purchase_post_sign_up &&
        this.isSubscriberOnly &&
        this.getAppProducts.length > 0
      )
    },

    shouldPostSignUpAction() {
      return !!this.appAccess.post_sign_up_action
    },

    shouldPostPurchaseScreen() {
      return (
        this.appAccess.is_subscriber_only &&
        !this.appAccess.allow_anonymous_browsing &&
        !this.$route.path.startsWith("/me/")
      )
    },

    postSignUpAction() {
      return this.appAccess.post_sign_up_action
    },

    shouldDisableClose() {
      return (
        !this.appAccess.allow_anonymous_browsing &&
        (this.appAccess.require_sign_in || this.appAccess.is_subscriber_only)
      )
    },

    ownerEmail() {
      return this.global?.account?.email
    },

    formattedCurrentProvider() {
      return capitalizeFirstLetter(this.currentProvider || "")
    },

    formattedProviderToLink() {
      return capitalizeFirstLetter(this.providerToLink || "")
    },

    enabledSocialProviders() {
      return this.providers.filter(
        p => ![AUTH_PROVIDERS.default, AUTH_PROVIDERS.passwordless].includes(p)
      )
    }
  },

  methods: {
    capitalizeFirstLetter,
    debounce,

    ...mapActions("auth", [
      "login",
      "signUp",
      "fetchUserAuth",
      "setPassword",
      "resetPassword",
      "requestResetPassword",
      "verifyPasswordlessAuth",
      "requestPasswordlessAuth",
      "fetchCurrentProvider",
      "linkProvider"
    ]),
    ...mapActions("analytics", ["trackActivity"]),
    ...mapActions("products", ["hasActivePurchase"]),

    saveLastEmail() {
      LocalStorage.set(LocalStorage.LS_KEYS.LAST_EMAIL, this.email)
    },

    attemptRequestPasswordless() {
      this.isLoading = true

      return this.requestPasswordlessAuth({ email: this.email })
        .then(() => this.openModal(STAGES.TOKEN_SENT))
        .catch(err => {
          if (err.reason === "password_required") {
            this.openModal(STAGES.LOGIN)
          } else {
            this.handleError(err)
          }
        })
    },

    handleCreateAccount({ password, name }) {
      this.password = password
      this.name = name

      if (this.isExistingAccount) {
        this.track("auth_setup_account")
        this.attemptSetPassword(name)
      } else {
        this.attemptSignUp()
      }
    },

    async handleExternalAuth({ provider, provider_id, email, name, avatar }) {
      this.isLoading = true

      const providerId = provider_id
      const currentEmail = this.isAuthenticated ? this.currentUser.email : email

      try {
        this.currentProvider = await this.fetchCurrentProvider({
          provider,
          email: currentEmail,
          providerId: provider_id
        })
      } catch (e) {
        console.error(e)

        this.$notify(
          `${capitalizeFirstLetter(
            provider
          )} has already been linked to an account. Please try a different method`
        )

        this.isLoading = false
        return
      }

      if (!this.currentProvider) {
        this.signUp({
          provider,
          providerId,
          email,
          name,
          avatar
        })
          .then(() => this.handleSuccessfullAuth(true))
          .catch(this.handleError)
      } else if (this.currentProvider === provider) {
        this.login({
          provider,
          providerId
        })
          .then(() => this.handleLogin())
          .catch(this.handleError)
      } else if (this.currentProvider === AUTH_PROVIDERS.passwordless) {
        // When the user is passwordless, we want to link their account to allow
        // them to login via this method in the future
        this.attemptLinkProvider({
          email: currentEmail,
          provider,
          providerId
        })
          .then(() =>
            this.login({ provider, providerId })
              .then(() => this.handleSuccessfullAuth(true))
              .catch(this.handleError)
          )
          .catch(e => {
            console.error(e)
            this.handleError({ message: `Unable to login with ${provider}` })
          })
      } else {
        this.isLoading = false

        this.email = email
        this.providerToLink = provider
        this.providerIdToLink = providerId

        this.openModal(STAGES.PROMPT_LINK_PROVIDER)
      }
    },

    attemptLinkProvider({ email, provider, providerId }) {
      return this.linkProvider({
        email,
        provider,
        providerId
      }).then(() => {
        this.$notify(
          `Successfully linked your ${capitalizeFirstLetter(provider)} account`
        )
      })
    },

    attemptSignUp() {
      if (this.email) {
        this.isLoading = true

        this.signUp({
          name: this.name,
          email: this.email,
          password: this.password
        })
          .then(() => this.handleSuccessfullAuth(true))
          .catch(this.handleError)
      } else {
        this.openModal(STAGES.EMAIL)
      }
    },

    attemptSetPassword(name = null) {
      this.isLoading = true

      this.setPassword({ name, password: this.password })
        .then(() => this.handleSuccessfullAuth())
        .catch(this.handleError)
    },

    attemptRequestResetPassword() {
      this.isLoading = true

      this.requestResetPassword({ email: this.email })
        .then(() => this.openModal(STAGES.TOKEN_SENT))
        .catch(this.handleError)
        .then(() => (this.isLoading = false))
    },

    attemptResetPassword({ password, passwordConfirmation }) {
      this.isLoading = true

      const { token } = this.$route.query

      this.resetPassword({
        token,
        password,
        passwordConfirmation
      })
        .then(() => {
          this.$notify(this.$t("components.authModal.resetPasswordSuccess"))
          this.closeModal()
        })
        .catch(this.handleError)
        .then(() => (this.isLoading = false))
    },

    attemptVerifyPasswordless() {
      const { auth_token } = this.$route.query

      this.verifyPasswordlessAuth({ token: auth_token })
        .then(user => {
          if (user.auth_provider === AUTH_PROVIDERS.passwordless) {
            this.isExistingAccount = true
            setTimeout(() => this.openModal(STAGES.SET_PASSWORD), 100)
          } else {
            this.handleLogin()
          }
        })
        .catch(err => {
          if (err.reason && err.reason === "password_required") {
            this.email = err.user_email
            setTimeout(() => this.openModal(STAGES.LOGIN), 100)
          } else {
            this.$notify(err.message)
            setTimeout(() => this.openModal(STAGES.PASSWORDLESS_ERROR), 100)
          }
        })
        .finally(() => {
          this.$router.replace({
            ...this.$router.currentRoute,
            query: { auth_token: undefined }
          })
        })
    },

    resetLinkingProvider() {
      this.providerToLink = null
      this.providerIdToLink = null

      this.openModal(STAGES.INITIAL)
    },

    async handleSuccessfullAuth(signUp = false) {
      if (this.providerToLink && !signUp) {
        this.isLinkingProvider = true

        try {
          await this.attemptLinkProvider({
            provider: this.providerToLink,
            providerId: this.providerIdToLink
          })
        } catch (e) {
          this.$notify(`Unable to link your ${this.providerToLink} account`)
        }

        this.isLinkingProvider = false
      }

      this.isLoading = false

      if (this.isExpressAuth) {
        this.closeModal()

        EventBus.$emit(EVENTS.POST_AUTH_FLOW)
      } else if (signUp && this.shouldPostSignUpAction) {
        this.closeModal()

        EventBus.$once(EVENTS.POSTING_SIGN_UP_ACTION)
        setTimeout(() => {
          triggerBlockAction(this.postSignUpAction, false, true)
        }, 150)
      } else if (!signUp && this.hasPastDueSubscription) {
        this.closeModal()

        setTimeout(() => {
          EventBus.$emit(EVENTS.TRIGGER_PAST_DUE_PROMPT, {})
        }, 250)
      } else if (this.shouldPromptPurchasePostSignup) {
        this.closeModal()

        findAsync(
          this.getAppProducts,
          async product => await this.hasActivePurchase(product.id)
        ).then(hasPurchased => {
          if (!hasPurchased) {
            EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
              productIds: this.getAppProducts.map(p => p.id),
              overlay: this.appPurchaseOverlayId,
              global: true
            })
          }
        })
      } else {
        this.openModal(STAGES.DOWNLOAD)
      }

      this.teardown()
    },

    handleError(err) {
      console.error(err)
      this.$notify(err.message)
      this.isLoading = false
    },

    fetchUser() {
      this.isLoading = true

      const args = { email: this.email }

      this.fetchUserAuth(args).then(res => {
        this.isExistingAccount = res.exists

        if (!res.exists) {
          if (this.isPrivateApp) {
            this.openModal(STAGES.NOT_RECOGNISED)
          } else {
            this.openModal(STAGES.SET_PASSWORD)
          }
        } else if (res.exists && res.passwordless) {
          this.attemptRequestPasswordless()
        } else {
          this.openModal(STAGES.LOGIN)
        }
      })
    },

    openModal(hash) {
      this.$router.push(hash)
    },

    handleInitialOpen() {
      this.track("auth_started")
    },

    trackAborted() {
      if (!this.isAuthenticated) {
        EventBus.$emit(EVENTS.AUTH_ABORTED)
        this.track("auth_aborted")
      }
    },

    handleModalClose() {
      this.teardown()
      if (!this.isAuthenticated) this.trackAborted()
    },

    handleLogin() {
      this.$notify(this.$t("components.authModal.loginSuccess"))
      this.handleSuccessfullAuth()
    },

    closeModal() {
      if (this.isClosing) return

      this.isClosing = true

      this.teardown()

      if (!this.isAuthenticated) this.trackAborted()
      this.$router.removeHash().then(() => (this.isClosing = false))
    },

    handleOpen(hash) {
      if (
        this.isAuthenticated &&
        ![
          STAGES.DOWNLOAD,
          STAGES.SET_PASSWORD,
          STAGES.PASSWORDLESS,
          STAGES.PASSWORDLESS_ERROR
        ].includes(hash)
      ) {
        this.closeModal()
      } else {
        this.isExpressAuth = EventBus.hasListener(EVENTS.POST_AUTH_FLOW)
      }

      if ([STAGES.LOGIN, STAGES.EMAIL].includes(hash)) {
        this.email = this.email.length
          ? this.email
          : LocalStorage.get(LocalStorage.LS_KEYS.LAST_EMAIL)
      }
    },

    goToEmail() {
      this.openModal(STAGES.EMAIL)

      setTimeout(() => {
        document.querySelector("[data-role=login-email]").focus()
      }, 500)
    },

    resendEmail() {
      this.isResendingEmail = true

      this.attemptRequestPasswordless()
        .then(() => {
          this.$notify(this.$t("components.authModal.emailSentResendSuccess"))
        })
        .catch(() => {
          this.$notify(this.$t("components.authModal.emailSentResendError"))
        })
        .finally(() => {
          this.isResendingEmail = false
          this.hasResentEmail = true
        })
    },

    track(activityType, data = {}) {
      this.trackActivity({ activityType, data })
    },

    teardown() {
      this.isLoading = false
      this.isLinkingProvider = false

      this.providerToLink = null
      this.providerIdToLink = null

      this.email = "" // Reset from LocalStorage on open

      this.password = ""
      this.passwordConfirmation = ""
    }
  },

  watch: {
    "$route.hash": {
      immediate: true,
      handler(hash) {
        if (Object.values(STAGES).includes(hash)) this.stage = hash
      }
    },

    isAuthenticated: {
      immediate: true,
      handler() {
        if (this.isAuthenticated) {
          this.isExistingAccount = true
          this.email = this.currentUser.email

          if ([STAGES.EMAIL, STAGES.INITIAL].includes(this.stage)) {
            this.closeModal()
          }

          if (
            this.stage === STAGES.SET_PASSWORD &&
            this.currentUser.auth_provider !== AUTH_PROVIDERS.passwordless
          ) {
            this.closeModal()
          }
        }
      }
    },

    stage: {
      immediate: true,
      handler(stage) {
        this.isLoading = false

        if (!this.stage) return

        if (stage && ![STAGES.EMAIL, STAGES.DOWNLOAD].includes(stage)) {
          this.track("auth_progressed", { stage })
        }

        if (stage === STAGES.DOWNLOAD) {
          if (!this.canPrompt) {
            this.closeModal()
          } else {
            this.track("auth_progressed", { stage })
          }
        }

        if (stage === STAGES.PASSWORDLESS && this.$route.query.auth_token) {
          this.attemptVerifyPasswordless()
        }

        if (
          stage === STAGES.NOT_RECOGNISED &&
          !this.isPrivateApp &&
          !this.email
        ) {
          this.openModal(STAGES.EMAIL)
        }

        if (
          stage === STAGES.SET_PASSWORD &&
          this.isAuthenticated &&
          this.currentUser.auth_provider !== AUTH_PROVIDERS.passwordless
        ) {
          this.closeModal()
        }

        if (
          stage === STAGES.INITIAL &&
          this.enabledSocialProviders.length === 0 &&
          this.$route.hash !== STAGES.EMAIL
        ) {
          this.openModal(STAGES.EMAIL)
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
.auth-modal__content {
  padding: 0 $size--4 $size--4;
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
